import { format, toZonedTime } from 'date-fns-tz'
import { cloneDeep } from 'lodash'

import Icon from '../../../components/Icons'
import ToggleField from '../../../components/FormLegacy/FormFieldsV2/ToggleField'
import DropdownDots from '../../../components/DropdownDots/DropdownDots'
import { generateId } from '../../../utils'
import StatusPill from '../../../components/StatusPill/StatusPill'
import Filter from '../../../components/Filter/Filter'
import { capitalize } from '../../../helpers/helpers'

import TextCell from './components/TextCell/TextCell'
import EditableDaysCell from './components/EditableDaysCell/EditableDaysCell'

export const getMessageTypeFilterOptions = (t) => [
  {
    id: 0,
    text: t('checklists-legacy:filters.messageType.all'),
    value: 'all',
    selectAll: true,
  },
  {
    id: 1,
    text: t('checklists-legacy:filters.messageType.moveInInspection'),
    value: 'move-in',
  },
  {
    id: 2,
    text: t('checklists-legacy:filters.messageType.moveOutInspection'),
    value: 'move-out',
  },
]

export const getChecklistTypeOptions = (t) => [
  {
    label: t('checklists-dashboard:typeOptions.moveIn'),
    value: 'move-in',
  },
  {
    label: t('checklists-dashboard:typeOptions.moveOut'),
    value: 'move-out',
  },
  {
    label: t('checklists-dashboard:typeOptions.task'),
    value: 'task',
  },
]

// kept for `.value`-only lookups (INITIAL_TASK_STATE, transformApiTaskToTaskState below) —
// use `getChecklistAssignTriggerTypeOptions(t)` wherever the `.label` is rendered
export const checklistAssignTriggerTypeOptions = [
  {
    label: 'Move-in',
    value: 'move-in',
  },
  {
    label: 'Move-out',
    value: 'move-out',
  },
  {
    label: 'Amenity Reservation Started',
    value: 'amenity-reservation-started',
  },
]

export const getChecklistAssignTriggerTypeOptions = (t) => [
  {
    label: t('checklists-create-task:assignTriggerTypeOptions.moveIn'),
    value: 'move-in',
  },
  {
    label: t('checklists-create-task:assignTriggerTypeOptions.moveOut'),
    value: 'move-out',
  },
  {
    label: t('checklists-create-task:assignTriggerTypeOptions.amenityReservationStarted'),
    value: 'amenity-reservation-started',
  },
]

export const getDateStartTriggers = (t) => [
  {
    id: 0,
    label: t('checklists-legacy:dateStartTriggers.moveInDate'),
    value: 'moveInDate',
  },
  {
    id: 1,
    label: t('checklists-legacy:dateStartTriggers.moveOutDate'),
    value: 'moveOutDate',
  },
]

export const getContinueStartTriggers = (t) => [
  {
    id: 0,
    label: t('checklists-create-task:continueStartTriggers.before'),
    value: 'before',
  },
  {
    id: 1,
    label: t('checklists-create-task:continueStartTriggers.after'),
    value: 'after',
  },
]

export const getManageInspectionTabs = (t) => [
  {
    id: 0,
    text: t('checklists-settings:manageInspectionTabs.setup'),
    value: 'setup',
  },
  {
    id: 1,
    text: t('checklists-settings:manageInspectionTabs.inspections'),
    value: 'inspections',
  },
  {
    id: 2,
    text: t('checklists-settings:manageInspectionTabs.custom'),
    value: 'custom',
  },
]

export const getChecklistStatusFilterOptions = (t) => [
  {
    id: 0,
    text: t('checklists-dashboard:statusFilterOptions.all'),
    value: '',
  },
  {
    id: 1,
    text: t('checklists-dashboard:statusFilterOptions.notStarted'),
    value: 'Not started',
  },
  {
    id: 2,
    text: t('checklists-dashboard:statusFilterOptions.inProgress'),
    value: 'In progress',
  },
  {
    id: 3,
    text: t('checklists-dashboard:statusFilterOptions.completed'),
    value: 'Completed',
  },
]

const getCustomTasksStatuses = (t) => [
  {
    id: 0,
    text: t('checklists-tasks:statuses.completed'),
    name: 'Completed',
  },
  {
    id: 1,
    text: t('checklists-tasks:statuses.notStarted'),
    name: 'Not Started',
  },
  {
    id: 2,
    text: t('checklists-tasks:statuses.pastDue'),
    name: 'Past Due',
  },
]

export const checklistsHistoryColumns = ({ onShowDetails = () => {}, timezone, t }) => {
  return [
    {
      Header: t('checklists-dashboard:history.residentNameEmailHeader'),

      Cell: ({ row: { original } }) => {
        const { userFullName, userEmail } = original
        return <TextCell title={userFullName} text={userEmail} />
      },
    },
    {
      Header: t('checklists-dashboard:history.unitNumberHeader'),
      accessor: 'clientApartment',
    },
    {
      Header: t('checklists-dashboard:history.moveInDateHeader'),
      accessor: 'moveInDate',
      Cell: ({ value }) =>
        value ? format(toZonedTime(value, timezone), 'MM/dd/yyyy', { timeZone: timezone }) : '-',
    },
    {
      Header: t('checklists-dashboard:history.statusHeader'),

      Cell: ({ row: { original } }) => {
        const { completionStatus, completionPercentage } = original
        return (
          <div className={`base-pill ${completionStatus.replace(/\s+/g, '')}`}>
            {`${completionStatus} ${completionPercentage}%`}
          </div>
        )
      },
    },
    {
      Header: t('checklists-dashboard:history.actionsHeader'),
      id: 'details_info',

      Cell: ({ row: { original } }) => {
        const { isActionRequired } = original
        return (
          <div className="actions-cell">
            <DropdownDots
              options={[
                {
                  id: 0,
                  text: t('checklists-dashboard:history.detailsOption'),
                  action: () => onShowDetails(original),
                },
              ]}
            />
            {!!isActionRequired && (
              <div className="base-pill">
                <Icon icon="exclamationPointTriangle" fill="#EE8E00" />
                {t('checklists-dashboard:history.actionRequired')}
              </div>
            )}
          </div>
        )
      },
    },
  ]
}

export const manageInspectionColumns = (
  isIntegratedYardi,
  removeChecklistTemplate,
  changeActiveStatus,
  setTemplate,
  role,
  t,
) => {
  const returnTable = [
    {
      Header: t('checklists-settings:manageInspectionColumns.templateNameHeader'),
      Cell: (data) => {
        return data.row.original.name
      },
    },
    {
      Header: t('checklists-settings:manageInspectionColumns.typeHeader'),
      Cell: (data) => {
        const { type } = data.row.original
        return getMessageTypeFilterOptions(t).find((option) => option.value === type).text || ''
      },
    },
    {
      Header: t('checklists-settings:manageInspectionColumns.unitTypeHeader'),
      Cell: (data) => {
        const { activeUnitTypes } = data.row.original
        const styles = {
          marginLeft: 5,
          fontSize: 10,
          borderRadius: 10,
          color: 'white',
          backgroundColor: '#B59B70',
          display: 'flex',
          alignItems: 'center',
          textAlign: 'center',
          padding: '0 7px',
        }
        return activeUnitTypes ? (
          activeUnitTypes.length > 2 ? (
            <div style={{ display: 'flex', flexDirection: 'row', cursor: 'pointer' }}>
              {activeUnitTypes
                .slice(2)
                .map((unit) => unit)
                .join(', ')}
              <div style={styles}>
                {t('checklists-settings:manageInspectionColumns.moreUnitTypes', {
                  num: activeUnitTypes.length - 2,
                })}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'row', cursor: 'pointer' }}>
              {activeUnitTypes.map((unit) => unit).join(', ')}
            </div>
          )
        ) : (
          '-'
        )
      },
    },
    {
      Header: '  ',
      Cell: (data) => {
        const dropdownOptions = (role) => {
          const options = [
            {
              id: 0,
              text: t('checklists-settings:manageInspectionColumns.viewOption'),
              action: () => setTemplate({ data: data.row.original, mode: 'preview' }),
            },
            {
              id: 1,
              text: t('checklists-settings:manageInspectionColumns.editOption'),
              action: () => setTemplate({ data: data.row.original, mode: 'edit' }),
            },
            {
              id: 2,
              text: t('checklists-settings:manageInspectionColumns.deleteOption'),
              action: () =>
                removeChecklistTemplate(data.row.original.id, data.row.original.isTemplate),
            },
          ]
          if (role && role !== 'Administrator') {
            return options.slice(0, 1)
          }
          return options
        }
        const styles = {
          border: '1px solid #EFEFEF',
          padding: '10px 5px',
          background: '#FBFBFB',
          borderRadius: 4,
          width: 130,
        }
        return (
          <DropdownDots
            label={t('checklists-settings:shared.actionsHeader')}
            options={dropdownOptions(role)}
            style={styles}
          />
        )
      },
    },
    {
      Header: t('checklists-settings:manageInspectionColumns.assignTriggerHeader'),
      accessor: 'assignTrigger',
      Cell: (data) => {
        const { id, assignTaskRelative, assignTaskDays, type } = data.row.original
        return (
          <EditableDaysCell
            fullTemplate={data.row.original}
            data={{
              id,
              condition: assignTaskRelative,
              trigger:
                getChecklistAssignTriggerTypeOptions(t).find((option) => option.value === type)
                  .label || '',
              days: assignTaskDays,
              defaultTrigger: true,
            }}
          />
        )
      },
    },
    {
      Header: t('checklists-settings:manageInspectionColumns.activatedHeader'),
      Cell: (data) => {
        const { active, id } = data.row.original
        return (
          <ToggleField
            id={`${id}`}
            name="table-toggle"
            onChange={() => changeActiveStatus(data.row.original)}
            value={active}
            disabled={!isIntegratedYardi}
          />
        )
      },
    },
  ]

  if (!isIntegratedYardi) delete returnTable[2]
  return returnTable
}

export const getItemStyle = (draggableStyle) => ({
  marginBottom: `8px`,
  display: 'flex',
  flexDirection: 'column',
  ...draggableStyle,
})

export const getListStyle = () => ({
  display: 'flex',
  flexDirection: 'column',
})

export const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)
  return result
}

export const residentTasksDetailsColumns = ({
  onShowDetails = () => {},
  setSelectedChecklist = () => {},
  onDeleteTask = () => {},
  setRemindTask = () => {},
  updateTaskStatus = () => {},
  navigate = () => {},
  timezone,
  t,
}) => {
  const customTasksStatuses = getCustomTasksStatuses(t)
  return [
    {
      Header: t('checklists-tasks:columns.taskNameHeader'),
      accessor: 'name',
    },
    {
      Header: t('checklists-tasks:columns.taskTypeHeader'),

      Cell: ({ row: { original } }) => {
        const { type } = original

        if (type === 'task') return capitalize(type)

        const title =
          type === 'task'
            ? t('checklists-tasks:columns.customType')
            : t('checklists-tasks:columns.inspectionType')

        return <TextCell title={title} text={capitalize(type)} />
      },
    },
    {
      Header: t('checklists-tasks:columns.dueDateHeader'),
      accessor: 'dueDate',
      Cell: ({ value }) =>
        value ? format(toZonedTime(value, timezone), 'MM/dd/yyyy', { timeZone: timezone }) : '-',
    },
    {
      Header: t('checklists-tasks:columns.statusHeader'),

      Cell: ({ row: { original } }) => {
        const { type, status } = original
        if (type === 'task') {
          const currentStatus = customTasksStatuses.find((element) => element.name === status)
          return (
            <Filter
              placeholder={status}
              options={customTasksStatuses}
              value={currentStatus ? [currentStatus] : []}
              variant="rounded"
              onChange={(e) => updateTaskStatus(original, e.target.value)}
              additionalClass={`resident-tasks-table-status-select  ${status.replace(/\s+/g, '')}`}
              name="status"
            />
          )
        } else {
          return <StatusPill title={status} />
        }
      },
    },
    {
      Header: t('checklists-tasks:columns.actionsHeader'),
      id: 'details_info',

      Cell: ({ row: { original } }) => {
        const { isActionRequired, type } = original

        const options = []

        switch (original.status) {
          case 'Approved':
            options.push({
              id: 0,
              text: t('checklists-tasks:columns.detailsOption'),
              action: () => {
                navigate(`/dashboard/building-dashboard/checklists/inspection/${original.id}`)
                setSelectedChecklist(original)
              },
            })
            break
          case 'Completed':
            options.push({
              id: 0,
              text: t('checklists-tasks:columns.detailsOption'),
              action: () => {
                if (type === 'task') {
                  onShowDetails(original)
                  return
                }

                navigate(`/dashboard/building-dashboard/checklists/inspection/${original.id}`)
                setSelectedChecklist(original)
              },
            })
            break
          case 'Started':
          case 'Not Started':
          case 'Past Due':
            options.push(
              {
                id: 0,
                text: t('checklists-tasks:columns.sendReminderOption'),
                action: () => setRemindTask(original),
              },
              {
                id: 1,
                text: t('checklists-tasks:columns.deleteTaskOption'),
                action: () => onDeleteTask(original),
              },
            )
            break
          case 'Pending Approval':
          case 'Revision Requested':
            options.push({
              id: 0,
              text: t('checklists-tasks:columns.previewOption'),
              action: () => {
                if (type === 'task') {
                  onShowDetails(original)
                  return
                }

                navigate(`/dashboard/building-dashboard/checklists/inspection/${original.id}`)
                setSelectedChecklist(original)
              },
            })
            break
          default:
            break
        }

        return (
          <div className="actions-cell">
            <DropdownDots options={options} />
            {isActionRequired && (
              <div className="base-pill">
                <Icon icon="exclamationPointTriangle" fill="#EE8E00" />
                {t('checklists-tasks:columns.actionRequired')}
              </div>
            )}
          </div>
        )
      },
    },
  ]
}

export const mapClientToUser = ({ id, apartment: unit, user, phone, creditCardId }) => ({
  value: id,
  userId: user.id,
  label: `${user.firstName} ${user.lastName}`,
  email: user.email,
  phone,
  unit,
  creditCardId,
})

export const getNamedHandler = (handleFieldChange) => {
  return (value, fieldName) => handleFieldChange({ target: { name: fieldName, value } })
}

export const getDataInputTypes = (isRealPage = false, inputs = [], t) => [
  {
    id: 0,
    label: t('checklists-create-task:dataInputTypes.shortAnswer'),
    value: 'short_answer',
  },
  {
    id: 1,
    label: t('checklists-create-task:dataInputTypes.longAnswer'),
    value: 'long_answer',
  },
  {
    id: 2,
    label: t('checklists-create-task:dataInputTypes.singleChoice'),
    value: 'single_choice',
  },
  {
    id: 3,
    label: t('checklists-create-task:dataInputTypes.multipleChoice'),
    value: 'multiple_choice',
  },
  {
    id: 4,
    label: t('checklists-create-task:dataInputTypes.fileUpload'),
    value: 'file_upload',
  },
  {
    id: 5,
    label: t('checklists-create-task:dataInputTypes.form'),
    value: 'form',
  },
  {
    id: 6,
    label: t('checklists-create-task:dataInputTypes.service'),
    value: 'service',
  },
  ...(isRealPage
    ? [
        {
          id: 7,
          label: t('checklists-create-task:dataInputTypes.realPagePet'),
          value: 'realpage_pet',
          disabled: inputs.some((input) => input.inputType === 'realpage_pet'),
        },
        {
          id: 8,
          label: t('checklists-create-task:dataInputTypes.realPageVehicle'),
          value: 'realpage_vehicle',
          disabled: inputs.some((input) => input.inputType === 'realpage_vehicle'),
        },
      ]
    : []),
  {
    id: 9,
    label: t('checklists-create-task:dataInputTypes.vehicleForm'),
    value: 'vehicle_form',
    disabled: inputs.some((input) => input.inputType === 'vehicle_form'),
  },
]

export const getTaskInfoTypes = (t) => [
  {
    id: 0,
    label: t('checklists-create-task:taskInfoTypes.textInfo'),
    value: 'text_info',
  },
  {
    id: 1,
    label: t('checklists-create-task:taskInfoTypes.providersInfo'),
    value: 'providers_info',
  },
]

export const maxFormEntriesOptions = [
  {
    id: 0,
    label: '1',
    value: 1,
  },
  {
    id: 1,
    label: '2',
    value: 2,
  },
  {
    id: 2,
    label: '3',
    value: 3,
  },
  {
    id: 3,
    label: '4',
    value: 4,
  },
  {
    id: 4,
    label: '5',
    value: 5,
  },
]

export const getProvidersTypeFilter = (t) => [
  {
    id: 0,
    text: t('checklists-create-task:providersTypeFilter.all'),
    value: 'all',
  },
  {
    id: 1,
    text: t('checklists-create-task:providersTypeFilter.water'),
    value: 'water',
  },
  {
    id: 2,
    text: t('checklists-create-task:providersTypeFilter.gas'),
    value: 'gas',
  },
  {
    id: 3,
    text: t('checklists-create-task:providersTypeFilter.electric'),
    value: 'electricity',
  },
  {
    id: 4,
    text: t('checklists-create-task:providersTypeFilter.tvAndInternet'),
    value: 'tv-and-internet',
  },
]

export const DATA_INPUT_ERROR_TYPES = {
  NO_LABEL: 'no_label',
  NO_CALL_TO_ACTION: 'no_call_to_action',
  EMPTY_OPTIONS: 'empty_options',
  OPTION_EMPTY_LABEL: 'option_empty_label',
  EMPTY_FORM: 'empty_form',
  EMPTY_PROVIDERS: 'empty_providers',
  EMPTY_TEXT_INFO: 'empty_text_info',
  NO_SERVICE_CTA: 'no_service_cta',
  NO_SERVICE: 'no_service',
}

export const validateDataInputs = (dataInputs, t) => {
  let isValid = true
  const newInputs = cloneDeep(dataInputs)
  newInputs.forEach((input) => {
    switch (input.inputType) {
      case 'short_answer':
      case 'long_answer':
      case 'file_upload':
        if (!input.taskLabel) {
          input.error = {
            type: DATA_INPUT_ERROR_TYPES.NO_LABEL,
            message: t('checklists-create-task:validation.taskLabelRequired'),
          }
          isValid = false
          break
        }

        input.error = null
        break
      case 'single_choice':
      case 'multiple_choice':
        if (!input.taskLabel) {
          input.error = {
            type: DATA_INPUT_ERROR_TYPES.NO_LABEL,
            message: t('checklists-create-task:validation.taskLabelRequired'),
          }
          isValid = false
          break
        }
        if (!input.options.length) {
          input.error = {
            type: DATA_INPUT_ERROR_TYPES.EMPTY_OPTIONS,
            message: t('checklists-create-task:validation.atLeastOneOptionRequired'),
          }
          isValid = false
          break
        }
        if (input.options.some((option) => !option)) {
          input.error = {
            type: DATA_INPUT_ERROR_TYPES.OPTION_EMPTY_LABEL,
            message: t('checklists-create-task:validation.fillEmptyOptionFields'),
          }
          isValid = false
          break
        }
        input.error = null
        break
      case 'form':
        if (!input.taskLabel) {
          input.error = {
            type: DATA_INPUT_ERROR_TYPES.NO_LABEL,
            message: t('checklists-create-task:validation.taskLabelRequired'),
          }
          isValid = false
          break
        }
        if (!input.formActionLabel) {
          input.error = {
            type: DATA_INPUT_ERROR_TYPES.NO_CALL_TO_ACTION,
            message: t('checklists-create-task:validation.actionLabelRequired'),
          }
          isValid = false
          break
        }
        if (!input.form.length) {
          input.error = {
            type: DATA_INPUT_ERROR_TYPES.EMPTY_FORM,
            message: t('checklists-create-task:validation.formStructureRequired'),
          }
          isValid = false
          break
        }
        input.error = null
        break
      case 'text_info':
        if (!input.textInfo) {
          input.error = {
            type: DATA_INPUT_ERROR_TYPES.EMPTY_TEXT_INFO,
            message: t('checklists-create-task:validation.textInfoRequired'),
          }
          isValid = false
          break
        }
        input.error = null
        break
      case 'providers_info':
        if (!input.providersInfo.length) {
          input.error = {
            type: DATA_INPUT_ERROR_TYPES.EMPTY_PROVIDERS,
            message: t('checklists-create-task:validation.atLeastOneProviderRequired'),
          }
          isValid = false
          break
        }
        input.error = null
        break
      case 'service':
        if (!input.taskLabel) {
          input.error = {
            type: DATA_INPUT_ERROR_TYPES.NO_LABEL,
            message: t('checklists-create-task:validation.taskLabelRequired'),
          }
          isValid = false
          break
        }
        if (!input.formActionLabel) {
          input.error = {
            type: DATA_INPUT_ERROR_TYPES.NO_SERVICE_CTA,
            message: t('checklists-create-task:validation.ctaLabelRequired'),
          }
          isValid = false
          break
        }

        if (input.formActionLabel.length > 20) {
          input.error = {
            type: DATA_INPUT_ERROR_TYPES.NO_SERVICE_CTA,
            message: t('checklists-create-task:validation.ctaLabelMaxLength'),
          }
          isValid = false
          break
        }
        if (!input.bookingTypeId) {
          input.error = {
            type: DATA_INPUT_ERROR_TYPES.NO_SERVICE,
            message: t('checklists-create-task:validation.selectServiceRequired'),
          }
          isValid = false
          break
        }
        input.error = null
        break
      default:
        break
    }
  })

  return {
    isValid,
    newInputs,
  }
}

export const INITIAL_TASK_STATE = {
  name: '',
  attachment: undefined,
  instructions: '',
  dueDateDays: 1,
  dueDateTrigger: checklistAssignTriggerTypeOptions[0].value,
  dueDateCondition: 'after',
  assignTaskTrigger: checklistAssignTriggerTypeOptions[1].value,
  assignTaskDays: 0,
  assignTaskRelative: 'before',
  approvalRequired: false,
  signatureRequired: false,
  assignTriggerVisibility: true,
  dueDateTriggerVisibility: true,
  limitTaskAssignmentVisibility: false,
  assignTaskStatusTrigger: null,
  customButtonEnabled: false,
  customButtonText: '',
  customButtonUrl: '',
  amenityId: null,
  inputs: [],
  integrationProvider: null,
  serviceTask: false,
}

export const DATA_INPUT = {
  taskLabel: '',
  isRequired: false,
  options: [],
  formActionLabel: '',
  formMaxEntries: 1,
  form: [],
  textInfo: '',
  providersInfo: [],
  error: null,
  yardiOtherOccupantCustomRecord: null,
  yardiResidentCustomRecord: null,
  bookingTypeId: null,
}

export const FORM_DATA_INPUT = {
  taskLabel: '',
  inputType: 'short_answer',
  isRequired: false,
  options: [],
  error: null,
}

export const transformApiTaskToTaskState = (data) => {
  return Object.keys(INITIAL_TASK_STATE).reduce((acc, key) => {
    switch (key) {
      case 'assignTriggerVisibility':
        return {
          ...acc,
          [key]: !!data.assignTaskTrigger,
          assignTaskTrigger: data.assignTaskTrigger
            ? data.assignTaskTrigger
            : checklistAssignTriggerTypeOptions[1].value,
          assignTaskDays: data.assignTaskDays ? data.assignTaskDays : 0,
          assignTaskRelative: data.assignTaskRelative ? data.assignTaskRelative : 'before',
        }
      case 'dueDateTriggerVisibility':
        return {
          ...acc,
          [key]: !!data.dueDateTrigger,
          dueDateDays: data.dueDateDays ? data.dueDateDays : 1,
          dueDateTrigger: data.dueDateTrigger
            ? data.dueDateTrigger
            : checklistAssignTriggerTypeOptions[0].value,
          dueDateCondition: data.dueDateCondition ? data.dueDateCondition : 'after',
        }
      case 'inputs':
        const inputs = (data[key] || []).map((input) => {
          const mapped = [...Object.keys(DATA_INPUT), 'inputType'].reduce((inputAcc, inputKey) => {
            switch (inputKey) {
              case 'form':
                return {
                  ...inputAcc,
                  form: (input['formItems'] || []).map((fi) => ({
                    ...fi,
                    options: fi.options ?? [],
                    _id: generateId(),
                  })),
                }
              case 'error':
                return { ...inputAcc, error: null }
              case 'bookingTypeId':
                return {
                  ...inputAcc,
                  bookingTypeId: input.bookingTypeId?.id ?? input.bookingTypeId ?? null,
                }
              default:
                // BE sends null for fields unused by the saved input type (e.g. `options` on a
                // form input); keep the DATA_INPUT default so switching the type doesn't crash
                return { ...inputAcc, [inputKey]: input[inputKey] ?? DATA_INPUT[inputKey] }
            }
          }, {})
          return { ...mapped, _id: generateId() }
        })
        return { ...acc, [key]: inputs }
      case 'limitTaskAssignmentVisibility':
        return {
          ...acc,
          [key]: !!data.assignTaskStatusTrigger,
          assignTaskStatusTrigger: data.assignTaskStatusTrigger,
        }
      default:
        return { ...acc, [key]: data[key] }
    }
  }, {})
}

export const formatResidentStatusesForFilter = (statuses) =>
  statuses.map((status) => ({
    id: status.id,
    label: status.status,
    value: status.id,
  }))

export const validateForm = ({
  formState,
  isIntegrationTask = false,
  tab,
  setNameError = () => {},
  setNoInputsError = () => {},
  setFormState = () => {},
  setCurrentTab = () => {},
  t,
}) => {
  const { name, inputs } = formState
  setNameError('')
  setNoInputsError('')
  if (!name) {
    setNameError(t('checklists-create-task:validation.pleaseEnterTaskName'))
    return false
  }

  if (!isIntegrationTask && inputs.length) {
    const { isValid, newInputs } = validateDataInputs(inputs, t)
    setFormState((prev) => ({ ...prev, inputs: newInputs }))

    if (!isValid) {
      setCurrentTab(tab)
    }

    return isValid
  }

  return true
}
